/**
 * Scalar units and small pure helpers for the voyage layer.
 *
 * UNITS, stated once so no other file has to guess:
 *  - `Leagues`   world distance. One league is a world-space unit. The render lane owns
 *                leagues -> pixels; nothing in src/voyage depends on that mapping.
 *  - `Progress`  the simulation's lifetime advancement scalar, dimensionless, monotone
 *                non-decreasing, 0 at a fresh save. Abbreviated `prog` in constant names.
 *  - `Millis`    wall-clock milliseconds, the same epoch the save timestamps use.
 *  - `Degrees`   compass bearing, 0 = +y (north), increasing clockwise, [0, 360).
 */

export type Leagues = number;
export type Progress = number;
export type Millis = number;
export type Degrees = number;

export interface Vec2 {
  readonly x: Leagues;
  readonly y: Leagues;
}

export function vec2(x: Leagues, y: Leagues): Vec2 {
  return { x, y };
}

export function distance(a: Vec2, b: Vec2): Leagues {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function magnitude(a: Vec2): Leagues {
  return Math.hypot(a.x, a.y);
}

/** 0 = north (+y), 90 = east (+x), clockwise, always in [0, 360). */
export function bearingDeg(from: Vec2, to: Vec2): Degrees {
  const d = (Math.atan2(to.x - from.x, to.y - from.y) * 180) / Math.PI;
  return ((d % 360) + 360) % 360;
}

export function headingDegFromVector(dx: number, dy: number): Degrees {
  const d = (Math.atan2(dx, dy) * 180) / Math.PI;
  return ((d % 360) + 360) % 360;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function clamp01(v: number): number {
  return clamp(v, 0, 1);
}

export function lerp(a: number, b: number, u: number): number {
  return a + (b - a) * u;
}

export function lerpVec2(a: Vec2, b: Vec2, u: number): Vec2 {
  return { x: lerp(a.x, b.x, u), y: lerp(a.y, b.y, u) };
}

/** C1-continuous ease. Closed form on purpose: nothing in this module integrates. */
export function smoothstep(u: number): number {
  const t = clamp01(u);
  return t * t * (3 - 2 * t);
}
